import type React from "react";
import { useId } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";

interface TextareaFieldProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	name: string;
	label?: string;
	placeholder?: string;
	tooltip?: React.ReactNode;
	inputType?: "text" | "email" | "password" | "date" | "number";
	display?: "stacked" | "inline";
	required?: boolean;
	invalid?: boolean;
	valid?: boolean;
}

export const TextareaField = ({
	name,
	label,
	placeholder,
	tooltip,
	display = "stacked",
	required = false,
	invalid = false,
	valid = false,
	...extraInputProps
}: TextareaFieldProps) => {
	const id = useId();

	return (
		<div
			className={`flex gap-2 flex-col ${
				display === "inline" && "sm:flex-row sm:items-center"
			}`}
		>
			{label && (
				<label
					htmlFor={name}
					className={`text-secondary-900 ${display === "inline" && "sm:w-1/3"}`}
				>
					{label}
					{required ? (
						""
					) : (
						<span className="ml-1 text-secondary-500">(optional)</span>
					)}
				</label>
			)}
			<div className={`relative ${display === "inline" && "sm:w-2/3"}`}>
				<textarea
					className={`border p-2 w-full bg-transparent rounded text-gray-900 ${
						invalid
							? "border-primary ring-primary-200 ring-4"
							: valid
								? "border-positive-500 ring-positive-100 ring-4"
								: "border-secondary-200"
					}`}
					name={name}
					placeholder={placeholder}
					aria-label={name}
					{...extraInputProps}
					data-tooltip-id={`tooltip-${id}`}
				/>
				{tooltip && (
					<ReactTooltip
						id={`tooltip-${id}`}
						className="text-left !bg-secondary !rounded-lg !text-white"
						place={"top"}
						openEvents={{ focus: true }}
						closeEvents={{ blur: true }}
					>
						{tooltip}
					</ReactTooltip>
				)}
			</div>
		</div>
	);
};
